import { ReactNode, useEffect, useState } from 'react';

import { GripVerticalIcon } from 'lucide-react';
import { Reorder, useDragControls, useMotionValue } from 'motion/react';

import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

/** Fixed drag-row height (Tailwind h-8 = 2rem = 32px), used to clamp the drag to the list. */
const REORDER_ROW_HEIGHT_PX = 32;

/** A single selectable/reorderable column. `key` must be unique and stable. */
export interface ReorderableColumn {
  key: string;
  label: string;
  checked: boolean;
  /** When true the checkbox is locked (e.g. the max-selection cap was reached). */
  disabled?: boolean;
  /** 1-based position in the overall column order, shown right-aligned. Optional. */
  position?: number;
}

interface ReorderableColumnSelectorProps {
  /** Ordered, draggable columns. The array order IS the display order. */
  columns: ReorderableColumn[];
  /** Toggle a column's checked state (by key). */
  onToggle: (key: string) => void;
  /**
   * Fired with the full new key order — only on drag end, NOT on every frame,
   * so consumers can persist / re-render heavy views without thrash. This is the
   * "order changed" listener callers hook into.
   */
  onReorder: (orderedKeys: string[]) => void;
  /** Element that opens the popover (e.g. an icon button). */
  trigger: ReactNode;
  /**
   * Columns pinned below the reorderable list under a divider — checkable but not
   * draggable (their position is fixed). Optional.
   */
  pinnedColumns?: ReorderableColumn[];
  /** Localized label for the fixed-section divider (required when pinnedColumns is set). */
  pinnedDividerLabel?: string;
  /** Optional hint shown at the top when the selection cap is hit (already-localized). */
  maxSelectedHint?: string;
  /** Accessible label for the drag handle (already-localized). */
  reorderHandleLabel: string;
  /** PopoverContent alignment. Defaults to "start". */
  align?: 'start' | 'center' | 'end';
  /** Extra classes for the PopoverContent (e.g. width). */
  contentClassName?: string;
}

/** Shared checkbox + label for both draggable and pinned rows. */
function ColumnCheckboxRow({
  column,
  leading,
  keyPrefix,
  onToggle,
}: {
  column: ReorderableColumn;
  leading: ReactNode;
  keyPrefix: string;
  onToggle: (key: string) => void;
}) {
  return (
    <>
      {leading}
      <Checkbox
        id={`${keyPrefix}-${column.key}`}
        checked={column.checked}
        disabled={column.disabled}
        onCheckedChange={() => onToggle(column.key)}
      />
      <Label
        htmlFor={`${keyPrefix}-${column.key}`}
        className={cn(
          'min-w-0 flex-1 truncate pl-1 text-sm font-normal leading-none',
          column.disabled && 'cursor-not-allowed opacity-70'
        )}
      >
        {column.label}
      </Label>
      {column.position !== undefined && (
        <span className="ml-2 flex-shrink-0 text-xs tabular-nums text-muted-foreground">
          {column.position}
        </span>
      )}
    </>
  );
}

/**
 * One draggable row. The drag `y` is a motion value we own and clamp in `onDrag`
 * to the list bounds (row index × row height). This is deterministic and — unlike
 * `dragConstraints` (ref OR numeric) — never fights Reorder's layout projection,
 * which otherwise makes rows vanish or the constraint frame drift.
 */
function DraggableColumnRow({
  column,
  index,
  rowCount,
  reorderHandleLabel,
  onToggle,
  onCommit,
}: {
  column: ReorderableColumn;
  index: number;
  rowCount: number;
  reorderHandleLabel: string;
  onToggle: (key: string) => void;
  onCommit: () => void;
}) {
  const dragControls = useDragControls();
  const y = useMotionValue(0);

  // How far this row may travel from its slot: up past the rows above, down past
  // the rows below — never beyond the list, so it can't reach the popover edges
  // (which would trigger scroll) or overlap a pinned section below.
  const minY = -index * REORDER_ROW_HEIGHT_PX;
  const maxY = (rowCount - 1 - index) * REORDER_ROW_HEIGHT_PX;

  return (
    <Reorder.Item
      as="div"
      value={column.key}
      dragListener={false}
      dragControls={dragControls}
      style={{ y }}
      onDrag={() => {
        const current = y.get();
        if (current < minY) y.set(minY);
        else if (current > maxY) y.set(maxY);
      }}
      onDragEnd={onCommit}
      transition={{ type: 'spring', stiffness: 600, damping: 40 }}
      className="relative flex h-8 select-none items-center gap-1 rounded-sm bg-popover px-2 py-1.5"
      whileDrag={{ scale: 1.02, zIndex: 1, boxShadow: '0 4px 12px hsl(var(--border))' }}
    >
      <ColumnCheckboxRow
        column={column}
        keyPrefix="column-selector"
        onToggle={onToggle}
        leading={
          <button
            type="button"
            aria-label={reorderHandleLabel}
            // Pointer-down on the handle hands the gesture to the reorder controls.
            // stopPropagation keeps the enclosing Radix popover from treating the
            // drag as an outside/dismiss interaction.
            onPointerDown={event => {
              event.stopPropagation();
              dragControls.start(event);
            }}
            className="flex h-5 w-4 flex-shrink-0 cursor-grab touch-none items-center justify-center text-muted-foreground active:cursor-grabbing"
          >
            <GripVerticalIcon size={14} />
          </button>
        }
      />
    </Reorder.Item>
  );
}

/**
 * Generic drag-to-reorder + show/hide column picker.
 *
 * State-controlled: the caller owns `columns` order + checked flags and reacts to
 * `onToggle` / `onReorder`. Internally the component keeps a local draft during a
 * drag so the reorder animates smoothly and `onReorder` fires ONCE, on drag end.
 *
 * See docs/ai/details/components.md → "Reorderable columns".
 */
export function ReorderableColumnSelector({
  columns,
  onToggle,
  onReorder,
  trigger,
  pinnedColumns,
  pinnedDividerLabel,
  maxSelectedHint,
  reorderHandleLabel,
  align = 'start',
  contentClassName,
}: ReorderableColumnSelectorProps) {
  // Local draft the Reorder.Group animates against during a drag. onReorder is
  // deferred to drag end so heavy consumers don't re-render every frame.
  const [draftKeys, setDraftKeys] = useState<string[]>(() => columns.map(column => column.key));
  const columnKeysFingerprint = columns.map(column => column.key).join(',');
  useEffect(() => {
    setDraftKeys(columns.map(column => column.key));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnKeysFingerprint]);

  const columnByKey = new Map(columns.map(column => [column.key, column]));
  const draftColumns = draftKeys
    .map(key => columnByKey.get(key))
    .filter((column): column is ReorderableColumn => column !== undefined);

  const commitOrder = () => onReorder(draftKeys);

  return (
    <Popover>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align={align}
        className={cn(
          'max-h-[var(--radix-popover-content-available-height)] w-48 overflow-y-auto overflow-x-hidden px-0 py-1',
          contentClassName
        )}
      >
        <div className="flex flex-col">
          {maxSelectedHint && (
            <p className="px-2 py-1.5 text-xs text-muted-foreground">{maxSelectedHint}</p>
          )}

          {/* Any pinned section is outside this group, so a row can never be dropped
              into it; each row's drag is clamped to the list bounds. */}
          <Reorder.Group
            as="div"
            axis="y"
            values={draftKeys}
            onReorder={setDraftKeys}
            className="flex flex-col"
          >
            {draftColumns.map((column, index) => (
              <DraggableColumnRow
                key={column.key}
                column={column}
                index={index}
                rowCount={draftColumns.length}
                reorderHandleLabel={reorderHandleLabel}
                onToggle={onToggle}
                onCommit={commitOrder}
              />
            ))}
          </Reorder.Group>

          {pinnedColumns && pinnedColumns.length > 0 && (
            <>
              <div className="my-1 flex items-center gap-2 px-2">
                <span className="h-px flex-1 bg-border" />
                {pinnedDividerLabel && (
                  <span className="text-[0.625rem] font-medium uppercase tracking-wide text-muted-foreground">
                    {pinnedDividerLabel}
                  </span>
                )}
                <span className="h-px flex-1 bg-border" />
              </div>
              {pinnedColumns.map(column => (
                <div key={column.key} className="flex h-8 items-center gap-1 px-2 py-1.5">
                  <ColumnCheckboxRow
                    column={column}
                    keyPrefix="column-selector-pinned"
                    onToggle={onToggle}
                    // Spacer aligns the pinned checkbox with the draggable rows'
                    // checkboxes (which sit after the grip handle).
                    leading={<span className="h-5 w-4 flex-shrink-0" aria-hidden />}
                  />
                </div>
              ))}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
